import * as React from 'react';
import Button from '@mui/material/Button';
import { alpha, styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ShareConfig from './ShareConfig'
import config from '../../../config'
import { Box, CircularProgress, Skeleton } from '@mui/material';
import { useHorsesStore } from '../../../stores/horses';
import LoadingButton from '@mui/lab/LoadingButton';
import { getEventByName } from './utils';
import _, { unset } from "lodash";
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
    '& .MuiButtonBase-root': {
        padding: '0 !important',
        backgroundColor: 'unset !important'
    },
    '& .MuiButton-contained': {
        backgroundColor: '#408697 !important'
    }
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

const BootstrapInput = styled(InputBase)(({ theme }) => ({
    'label + &': {
        marginTop: theme.spacing(3),
    },
    '& .MuiInputBase-input': {
        borderRadius: 4,
        position: 'relative',
        backgroundColor: theme.palette.mode === 'light' ? '#fcfcfb' : '#2b2b2b',
        border: '1px solid #ced4da',
        fontSize: 12,
        padding: '5px 7px',
        transition: theme.transitions.create([
            'border-color',
            'background-color',
            'box-shadow',
        ]),
        // Use the system font instead of the default Roboto font.
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
        '&:focus': {
            boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
            borderColor: theme.palette.primary.main,
        },
    },
}));
const App: React.FC<{ open: boolean, close: () => void, shareKey: string, profileId?: string }> = (props) => {
    const stores = useHorsesStore()
    const [text, setText] = React.useState(props.profileId ? `${config.url}/share-profile/#stores.sharingKey}` : `Profile has not been created.`)
    const [loading, setLoading] = React.useState(false)
    const [shareItem, setShareItem] = React.useState<Array<number | string>>()
    const prevCountRef = React.useRef<Array<number | string>>();

    React.useEffect(() => {
        if (props.profileId && props.open == true) {
            setLoading(true)
            stores.getSharePage(props.profileId).then(key => {
                if (key) {
                    setText(`${config.url}/share-profile/#/${key[0].sharing_key}`)
                } else {
                    setText(`${config.url}/share-profile/#/${stores.sharingKey}`)
                }
                setLoading(false)
            }).catch(err => {
                setLoading(false)
            })
        }
    }, [props.open])

    const checkBoxChanged = React.useCallback((value: Object) => {
        const eventsId = Object.keys(value).reduce<Array<number | string>>((prev, key) => {
            if (value[key] === true) {
                const name = getEventByName({ name: key })
                if (name) {
                    prev.push(name)
                }
            }

            return prev
        }, [])
        setShareItem(eventsId);
    }, [])

    const save = React.useCallback(() => {
        stores.share?._ID && shareItem && stores.updateSharePage(stores.share?._ID, shareItem).then(_ => {
            props.close();
        })
    }, [shareItem])

    React.useEffect(() => {
        prevCountRef.current = shareItem
    }, [shareItem])

    const isChanged = React.useMemo(() => {
        return !_.isEqual(shareItem, prevCountRef.current)
    }, [shareItem])
    return (
        <BootstrapDialog
            onClose={props.close}
            aria-labelledby="customized-dialog-title"
            open={props.open}
            sx={{
                "& Button": {
                    padding: '0px',
                    background: 'transparent ',
                    color: '#408697 ',
                    borderColor: '#bdbdbd ',
                    marginLeft: '10px',
                    fontSize: '12px',

                },
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'column',
            }}
        >
            <BootstrapDialogTitle id="customized-dialog-title" onClose={props.close}>
                Share Profile
            </BootstrapDialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" gutterBottom>
                    Share Link
                </Typography>
                {loading == true ?
                    <>
                        <Skeleton variant="text" >
                            <Typography variant="caption" display="block">
                                You can share the profile of your horse.
                            </Typography>
                        </Skeleton>
                        <Skeleton variant="rounded"  >
                            <Box alignItems={'center'} justifyContent="center" display={'flex'}>
                                <Skeleton variant="rounded"  >
                                    <BootstrapInput fullWidth onChange={(val) => setText(val.target.value)} value={text} id="bootstrap-input"
                                        inputProps={{
                                            startAdornment: <RestartAltIcon ></RestartAltIcon>,
                                        }}
                                    />
                                </Skeleton>
                                <Skeleton variant="rounded"  >
                                    <Button autoFocus onClick={() => { navigator.clipboard.writeText(text) }} variant='outlined'>
                                        Copy
                                    </Button>
                                </Skeleton>

                            </Box>
                        </Skeleton>
                    </> :
                    <>
                        <Typography variant="caption" display="block">
                            You can share the profile of your horse.
                        </Typography>
                        <Box alignItems={'center'} justifyContent="flex-start" display={'flex'}>
                            <BootstrapInput fullWidth onChange={(val) => setText(val.target.value)} value={text} id="bootstrap-input"
                                inputProps={{
                                    startAdornment: <RestartAltIcon ></RestartAltIcon>,
                                }}
                            />
                            <Button autoFocus onClick={() => { navigator.clipboard.writeText(text) }} variant='outlined'>
                                Copy
                            </Button>

                        </Box>
                        <ShareConfig onChange={checkBoxChanged} />
                    </>
                }



            </DialogContent>
            <DialogActions>
                <OkButton
                    fullWidth
                    sx={{
                        margin: 0,
                        ".MuiButtonBase-root": {
                            backgroundColor: '#408697 !important',
                        },

                        ".Mui-disabled": {
                            background: '#bfbfbf !important'
                        }
                    }}
                    variant='contained'
                    autoFocus
                    onClick={save}
                    disabled={isChanged == true ? stores.share?._ID && shareItem ? false : true : true}>
                    Update
                </OkButton>
            </DialogActions>
        </BootstrapDialog >
    );
}
const OkButton = styled(LoadingButton)`
 width: 100% !important;
    color:#fff !important;
    background: #408697 !important;
    font-family: sans-serif !important;
   
    
`
export default App